import React, { useState, useEffect, useCallback } from 'react';
import { Package, DollarSign, ShoppingCart, Clock, Check, X, AlertCircle, ChevronDown, ChevronUp, Send, ExternalLink, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const VendorPackages = ({ user, onUserUpdate, t, filterVendor, onClose, language }) => {
  const [packages, setPackages] = useState({ ryder: [], speedos: [] });
  const [ilsRate, setIlsRate] = useState(3.6);
  const [myCoupons, setMyCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(filterVendor || 'ryder');
  const [showHistory, setShowHistory] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState(false);

  const loadPackages = useCallback(async () => {
    try {
      const response = await axios.get('/api/vendor');
      setPackages(response.data.packages);
      setIlsRate(response.data.ilsRate);
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  }, []);

  const loadMyCoupons = useCallback(async () => {
    try {
      const response = await axios.get('/api/vendor/my-coupons');
      setMyCoupons(response.data);
    } catch (error) {
      console.error('Error loading coupons:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadPackages(), loadMyCoupons()]);
      setLoading(false);
    };
    loadData();
  }, [loadPackages, loadMyCoupons]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setPurchasing(true);
    try {
      const response = await axios.post('/api/vendor', {
        vendor: selectedPackage.vendor,
        packageCategory: selectedPackage.category,
        priceILS: selectedPackage.priceILS
      });

      toast.success(t?.('packagePurchased') || 'Package purchased successfully!');
      setSelectedPackage(null);

      if (onUserUpdate) {
        onUserUpdate({ ...user, points: response.data.newBalance });
      }

      loadMyCoupons();
    } catch (error) {
      const message = error.response?.data?.message || (t?.('purchaseFailed') || 'Failed to purchase package');
      toast.error(message);
    } finally {
      setPurchasing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return { bg: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' };
      case 'redeemed': return { bg: 'rgba(96, 165, 250, 0.15)', color: '#60a5fa' };
      case 'confirmed': return { bg: 'rgba(52, 211, 153, 0.15)', color: '#34d399' };
      case 'cancelled': return { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' };
      default: return { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={12} />;
      case 'redeemed': return <Check size={12} />;
      case 'confirmed': return <Check size={12} />;
      case 'cancelled': return <X size={12} />;
      default: return null;
    }
  };

  const getVendorContact = (vendor) => {
    const contacts = {
      speedos: { handle: '@speedoscodm', link: 'https://t.me/speedoscodm' },
      ryder: { handle: '@ryderont', link: 'https://t.me/ryderont' }
    };
    return contacts[vendor] || null;
  };

  const translateCategory = (category) => {
    if (language === 'he') {
      const heTranslations = {
        'Up to two weeks': 'עד שבועיים',
        'Up to a week': 'עד שבוע',
        'Up to 48 Hours': 'עד 48 שעות',
        'Up to 24 Hours': 'עד 24 שעות',
        'Up to 3 hours': 'עד 3 שעות',
        'Minutes to 24 Hours': 'דקות עד 24 שעות'
      };
      return heTranslations[category] || category;
    }
    if (language === 'ar') {
      const arTranslations = {
        'Up to two weeks': 'حتى أسبوعين',
        'Up to a week': 'حتى أسبوع',
        'Up to 48 Hours': 'حتى 48 ساعة',
        'Up to 24 Hours': 'حتى 24 ساعة',
        'Up to 3 hours': 'حتى 3 ساعات',
        'Minutes to 24 Hours': 'دقائق إلى 24 ساعة'
      };
      return arTranslations[category] || category;
    }
    return category;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
        {t?.('loading') || 'Loading...'}
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
      borderRadius: '12px',
      padding: '24px',
      border: '2px solid #8b5cf6'
    }}>
      {/* Back Button when filtered */}
      {filterVendor && onClose && (
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "transparent",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            padding: "8px 0",
            marginBottom: "12px",
            fontSize: "14px"
          }}
        >
          <ArrowLeft size={18} />
          {(language === "he" ? "חזור" : language === "ar" ? "رجوع" : "Back")}
        </button>
      )}

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h3 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #a78bfa, #c4b5fd, #a78bfa)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <Package size={28} style={{ color: '#a78bfa' }} />
          {t?.('vendorPackages') || 'Vendor Packages'}
        </h3>
        <p style={{ color: '#9ca3af', fontSize: '14px' }}>
          {t?.('vendorPackagesDescription') || 'Purchase packages from our partner vendors'}
        </p>
        <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
          {t?.('currentRate') || 'Current rate'}: 1 USD = {ilsRate.toFixed(2)} {language === 'he' ? 'ש"ח' : 'ILS'}
        </p>
      </div>      {/* Vendor Tabs - hidden when filtered */}
      {!filterVendor && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          {['ryder', 'speedos'].map(vendor => (
            <button
              key={vendor}
              onClick={() => setExpandedVendor(vendor)}
              style={{
                flex: 1,
                padding: '12px',
                background: expandedVendor === vendor ? '#4c1d95' : '#1f2937',
                border: `2px solid ${expandedVendor === vendor ? '#8b5cf6' : '#374151'}`,
                borderRadius: '8px',
                color: expandedVendor === vendor ? '#fff' : '#9ca3af',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'all 0.2s'
              }}
            >
              {vendor.charAt(0).toUpperCase() + vendor.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Packages Grid */}
      <div style={{ marginBottom: '20px' }}>
        {packages[expandedVendor]?.map(pkg => (
          <div
            key={translateCategory(pkg.category)}
            style={{
              background: '#111827',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '12px',
              border: '1px solid #374151'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Package size={20} style={{ color: '#a78bfa' }} />
                <span style={{ fontWeight: '600', color: '#fff', fontSize: '16px' }}>
                  {translateCategory(pkg.category)}
                </span>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '10px'
            }}>
              {pkg.pricesWithPoints?.map(price => {
                const isSelected = selectedPackage?.vendor === expandedVendor &&
                  selectedPackage?.category === pkg.category &&
                  selectedPackage?.priceILS === price.priceILS;
                const canAfford = user.points >= price.pointsRequired;

                return (
                  <button
                    key={price.priceILS}
                    onClick={() => {
                      if (canAfford) {
                        setSelectedPackage({
                          vendor: expandedVendor,
                          category: pkg.category,
                          priceILS: price.priceILS,
                          pointsRequired: price.pointsRequired
                        });
                      }
                    }}
                    disabled={!canAfford}
                    style={{
                      padding: '12px',
                      background: isSelected ? '#4c1d95' : canAfford ? '#1f2937' : '#111827',
                      border: `2px solid ${isSelected ? '#8b5cf6' : canAfford ? '#374151' : '#1f2937'}`,
                      borderRadius: '8px',
                      cursor: canAfford ? 'pointer' : 'not-allowed',
                      opacity: canAfford ? 1 : 0.5,
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '16px' }}>
                      {price.priceILS} {language === 'he' ? 'ש"ח' : 'ILS'}
                    </div>
                    <div style={{
                      color: canAfford ? '#a78bfa' : '#6b7280',
                      fontSize: '13px',
                      marginTop: '4px'
                    }}>
                      {price.pointsRequired.toLocaleString()} {t?.('pts') || 'pts'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Purchase Button */}
      {selectedPackage && (
        <div style={{
          background: '#111827',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '20px',
          border: '2px solid #8b5cf6'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                {t?.('selectedPackage') || 'Selected Package'}
              </div>
              <div style={{ color: '#fff', fontWeight: '600' }}>
                {selectedPackage.vendor.charAt(0).toUpperCase() + selectedPackage.vendor.slice(1)} - {translateCategory(selectedPackage.category)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#fff', fontWeight: '600', fontSize: '18px' }}>
                {selectedPackage.priceILS} {language === 'he' ? 'ש"ח' : 'ILS'}
              </div>
              <div style={{ color: '#a78bfa', fontSize: '14px' }}>
                {selectedPackage.pointsRequired.toLocaleString()} {t?.('points') || 'points'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setPurchaseModal(true)}
            disabled={purchasing}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: '600',
              fontSize: '16px',
              cursor: purchasing ? 'wait' : 'pointer',
              opacity: purchasing ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <ShoppingCart size={18} />
            {purchasing ? (t?.('processing') || 'Processing...') : (t?.('purchasePackage') || 'Purchase Package')}
          </button>
          <p style={{
            color: '#6b7280',
            fontSize: '12px',
            textAlign: 'center',
            marginTop: '8px'
          }}>
            {t?.('vendorNote') || 'The vendor will process your request and provide the package.'}
          </p>
        </div>
      )}

      {/* Purchase History Toggle */}
      <button
        onClick={() => setShowHistory(!showHistory)}
        style={{
          width: '100%',
          padding: '12px',
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={16} />
          {t?.('purchaseHistory') || 'Purchase History'}
          {myCoupons.length > 0 && (
            <span style={{
              background: '#374151',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px'
            }}>
              {myCoupons.length}
            </span>
          )}
        </span>
        {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* Purchase History */}
      {showHistory && (
        <div style={{ marginTop: '12px' }}>
          {myCoupons.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '30px',
              color: '#6b7280'
            }}>
              <AlertCircle size={24} style={{ marginBottom: '8px' }} />
              <p>{t?.('noPurchasesYet') || 'No purchases yet'}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {myCoupons.map(coupon => {
                const statusStyle = getStatusColor(coupon.status);
                return (
                  <div
                    key={coupon._id}
                    style={{
                      background: '#111827',
                      borderRadius: '8px',
                      padding: '12px',
                      borderLeft: `3px solid ${statusStyle.color}`
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <span style={{ color: '#fff', fontWeight: '500' }}>
                          {coupon.vendor.charAt(0).toUpperCase() + coupon.vendor.slice(1)} - {translateCategory(coupon.packageCategory)}
                        </span>
                        <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                          {coupon.priceILS} {language === 'he' ? 'ש"ח' : 'ILS'} • {coupon.pointsDeducted.toLocaleString()} {t?.('pts') || 'pts'} • {formatDate(coupon.createdAt)}
                        </div>
                      </div>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {getStatusIcon(coupon.status)}
                        {coupon.status.charAt(0).toUpperCase() + coupon.status.slice(1)}
                      </span>
                    </div>
                    {coupon.couponCode && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: 'rgba(167, 139, 250, 0.2)',
                        borderRadius: '8px',
                        display: 'inline-block'
                      }}>
                        <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '4px' }}>{t?.('couponCode') || 'Coupon Code'}</div>
                        <div style={{ color: '#a78bfa', fontWeight: '700', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '1px' }}>{coupon.couponCode}</div>
                      </div>
                    )}
                    {coupon.status === 'pending' && (
                      <div style={{
                        color: '#fbbf24',
                        fontSize: '12px',
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <Clock size={12} />
                        {t?.('awaitingVendor') || 'Awaiting vendor processing'}
                      </div>
                    )}
                    {coupon.status === 'cancelled' && coupon.pointsRefunded && (
                      <div style={{
                        color: '#ef4444',
                        fontSize: '12px',
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <AlertCircle size={12} />
                        {t?.('pointsRefunded') || 'Points have been refunded'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Purchase Confirmation Modal */}
      {purchaseModal && selectedPackage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setPurchaseModal(false)}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            border: '2px solid #8b5cf6'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', marginBottom: '16px', textAlign: 'center' }}>
              {t?.('confirmPurchase') || 'Confirm Purchase'}
            </h3>
            <p style={{ color: '#9ca3af', textAlign: 'center', marginBottom: '20px' }}>
              {t?.('confirmPurchaseText') || 'Are you sure you want to purchase this package?'}
            </p>
            <div style={{ background: '#111827', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <p style={{ color: '#a78bfa', fontWeight: '600', marginBottom: '8px' }}>
                {selectedPackage.vendor.charAt(0).toUpperCase() + selectedPackage.vendor.slice(1)} - {translateCategory(selectedPackage.category)}
              </p>
              <p style={{ color: '#fff', fontSize: '20px', fontWeight: '700' }}>{selectedPackage.priceILS} {language === 'he' ? 'ש"ח' : 'ILS'}</p>
              <p style={{ color: '#f59e0b', fontSize: '14px' }}>{selectedPackage.pointsRequired.toLocaleString()} {t?.('points') || 'points'}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setPurchaseModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {t?.('no') || 'No'}
              </button>
              <button
                onClick={() => { setPurchaseModal(false); handlePurchase(); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#7c3aed',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {t?.('yesPurchase') || 'Yes, Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorPackages;
